"use client";
import "@/styles/carousel.style.css";
import Autoplay from "embla-carousel-autoplay";
import { cn } from "@/lib/utils/utils";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import Image from "next/image";
import { HoverButton } from "./hoverButton";
import useLocalizer from "@/hooks/useLocalizer";
import { Link } from "@/i18n/routing";
import { Button } from "../ui/button";
import { Activity, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Flex from "../core/flex";
import Text from "./text";

type ScaledCarouslProps = {
  useControls?: boolean;
  autoPlay?: boolean;
  delay?: number;
  title?: string;
  items: {
    image: string;
    title?: string;
    description?: string;
    href?: string;
  }[];
  className?: string;
  startScaled?: boolean;
};

const formatDescription = (desc?: string) => {
  if (!desc) return null;
  const words = desc.split(" ");
  if (words.length <= 1) return desc;
  return (
    <>
      <Text as="h1" className="block font-bold">
        {words.slice(0, 1).join(" ")}
      </Text>
      <Text as="h1" className="block font-bold">
        {words.slice(1, 2).join(" ")}
      </Text>
      <Text as="h1" className="block font-bold">
        {words.slice(2).join(" ")}
      </Text>
    </>
  );
};

const CarouselButton: React.FC<
  Pick<
    React.ComponentProps<typeof Button>,
    "variant" | "onClick" | "className" | "disabled"
  > & {
    direction: "prev" | "next";
  }
> = ({ variant, onClick, className, direction, disabled }) => {
  const { isRtl, t } = useLocalizer();

  const ariaLabel =
    direction === "prev" ? t("buttons.previous") : t("buttons.next");

  return (
    <Button
      onClick={onClick}
      variant={variant}
      className={cn(
        "rounded-none h-44 w-14 bg-white/30 text-white font-bold ",
        className,
      )}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {isRtl ? (
        direction === "prev" ? (
          <ChevronLeft size={24} />
        ) : (
          <ChevronRight size={24} />
        )
      ) : direction === "prev" ? (
        <ChevronLeft size={24} />
      ) : (
        <ChevronRight size={24} />
      )}
    </Button>
  );
};

const CarouselControls = ({
  api,
  className,
}: {
  api: CarouselApi;
  className?: string;
}) => {
  const [count, setCount] = useState<number>(0);
  const [current, setCurrent] = useState<number>(0);
  const { isRtl } = useLocalizer();

  const canScrollNext = useMemo(() => {
    return current != count;
  }, [current, count]);

  const canScrollPrev = useMemo(() => {
    return current != 1;
  }, [current]);

  useEffect(() => {
    if (!api) {
      return;
    }
    setCount(api.scrollSnapList()?.length);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  return (
    <Flex
      direction="row"
      justify="between"
      items="center"
      className={cn(
        "w-full absolute top-[50%] translate-y-[-50%] left-0 right-0 z-50",
        className,
      )}
    >
      <CarouselButton
        direction="prev"
        variant="normal"
        className="hover:text-secondary"
        onClick={() => (isRtl ? api?.scrollPrev() : api?.scrollNext())}
        disabled={!canScrollPrev}
      />
      <CarouselButton
        direction="next"
        variant="normal"
        className="hover:text-secondary"
        onClick={() => (isRtl ? api?.scrollNext() : api?.scrollPrev())}
        disabled={!canScrollNext}
      />
    </Flex>
  );
};

const ScaledCarousl: React.FC<ScaledCarouslProps> = ({
  useControls = true,
  autoPlay = true,
  delay = 5000,
  title,
  items,
  className,
  startScaled = false,
}) => {
  const { t } = useLocalizer();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState<number>(0);

  useEffect(() => {
    if (!api) {
      return;
    }
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api, current, startScaled]);

  return (
    <div
      dir="ltr"
      className={cn("w-full h-[calc(100vh-100px)] relative -top-10", className)}
    >
      <Carousel
        setApi={setApi}
        plugins={autoPlay ? [Autoplay({ delay })] : []}
        className="w-full h-full relative"
      >
        {useControls && <CarouselControls api={api} />}
        <CarouselContent>
          {items.map((item, index) => (
            <CarouselItem
              key={index}
              className="w-full h-[calc(100vh-100px)] relative select-none overflow-hidden"
            >
              <Image
                src={item.image}
                alt="scaledCarousl"
                fill
                priority={index === 0}
                placeholder="empty"
                className={cn(
                  "object-cover transition-all duration-300 ease-in-out ",
                  current === index && startScaled ? "scale-img" : "",
                )}
              />
              <div className=" w-full h-full inset-0 absolute bg-black opacity-60" />
              <Flex
                direction="col"
                center
                className=" h-full w-full absolute z-50"
              >
                <Image
                  src="/images/main-slider-icon.png"
                  alt="main-slider-icon"
                  width={50}
                  height={50}
                  placeholder="empty"
                  priority
                  className={cn(
                    "transition-all duration-500 ease-in mb-10 delay-200 -translate-y-full",
                    current === index && "translate-y-0",
                  )}
                />
                <div className="carousel-item-container">
                  <Text className="carousel-item-title">{title}</Text>
                </div>
                <Activity mode={item?.title ? "visible" : "hidden"}>
                  <div className="text-white text-center  flex justify-center items-center flex-col mt-8 mx-5 line-clamp-3">
                    {formatDescription(item.title)}
                  </div>
                </Activity>
                <Activity mode={item?.description ? "visible" : "hidden"}>
                  <Text
                    as="p"
                    className="text-white text-center flex justify-center items-center mt-8 mx-5 line-clamp-3"
                  >
                    {item.description}
                  </Text>
                </Activity>
                <Activity mode={item?.href ? "visible" : "hidden"}>
                  <Link
                    href={item?.href ?? "#"}
                    aria-label={`${t("buttons.discoverMore")}: ${item.description || title}`}
                    className={cn(
                      "my-10 transition-all duration-500 ease-in delay-200 translate-y-full",
                      current === index && "translate-y-0",
                    )}
                  >
                    <HoverButton
                      variant="normal"
                      className="min-w-[200px] h-16 text-lg "
                    >
                      {t("buttons.discoverMore")}
                    </HoverButton>
                  </Link>
                </Activity>
              </Flex>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  );
};
export default ScaledCarousl;
